import { RotateCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { UpgradeBadge } from '@/components/subscription/FeatureGate';
import { ContextMenuItem } from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { RerunChoice, RerunChoiceKey } from '@/lib/analyses/comparisonRerunChoices';
import { translateKey } from '@/lib/analyses/translateKey';
import { cn } from '@/lib/utils';

/** Same identity for the memoised list rows: the key and the gate of each
 *  choice (gates are `FEATURE_GATES` constants). */
export const sameRerunChoices = (a: readonly RerunChoice[], b: readonly RerunChoice[]): boolean =>
  a === b || (a.length === b.length && a.every((c, i) => c.key === b[i].key && c.lock?.gate === b[i].lock?.gate));

/** A locked choice is muted with its plan badge but stays selectable (never
 *  `disabled`): selecting it opens the upgrade dialog. */
function ChoiceContent({ choice }: { choice: RerunChoice }) {
  const { t } = useTranslation('project');
  return (
    <>
      <RotateCw className={cn(choice.lock && 'opacity-50')} />
      <span className={cn('min-w-0 flex-1', choice.lock && 'opacity-60')}>{translateKey(t, choice.labelKey)}</span>
      {choice.lock && <UpgradeBadge gate={choice.lock.gate} />}
    </>
  );
}

/** The re-run choices of a paired comparison as a dropdown on `trigger` (the
 *  row's icon button, the drawer's footer button). */
export function ComparisonRerunDropdown({
  choices,
  onChoose,
  children,
}: {
  choices: readonly RerunChoice[];
  onChoose: (key: RerunChoiceKey) => void;
  /** The trigger (rendered `asChild`). */
  children: ReactNode;
}) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-72'>
        {choices.map((choice) => (
          <DropdownMenuItem
            key={choice.key}
            data-testid={`analysis-run-rerun-${choice.key}`}
            data-locked={choice.lock ? 'true' : undefined}
            onSelect={() => onChoose(choice.key)}>
            <ChoiceContent choice={choice} />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The same choices as context-menu items. */
export function ComparisonRerunContextItems({
  choices,
  onChoose,
}: {
  choices: readonly RerunChoice[];
  onChoose: (key: RerunChoiceKey) => void;
}) {
  return (
    <>
      {choices.map((choice) => (
        <ContextMenuItem
          key={choice.key}
          data-testid={`analysis-run-context-rerun-${choice.key}`}
          data-locked={choice.lock ? 'true' : undefined}
          onSelect={() => onChoose(choice.key)}>
          <ChoiceContent choice={choice} />
        </ContextMenuItem>
      ))}
    </>
  );
}
